/**
 * Typed command names for the design DocumentStore façade. Each mirrors a
 * current mutation action on `SiteActions` (see `src/actions/SiteActions.js`);
 * `DocumentStore.dispatch` routes them to that action (pass-through) in this
 * phase. See `commandMap.js` for the routing table.
 * The list is derived from every entry in `SiteActions.js`'s
 * `Reflux.createActions({...})` block, EXCLUDING:
 * - the async loaders `loadPage`/`loadSite` (not mutations)
 * - actions with no design-mutation effect: `addBlockCompleted`,
 *   `addSiteBlockCompleted`, `replaceBlockCompleted`,
 *   `replaceSiteBlockCompleted` (only close an admin dialog, see
 *   `src/actions/AdminActions.js`), `afterLoadSiteVersionCheck`, `copy`
 *   (clipboard-only, no design mutation), `pageLoaded`, `previewDesign`,
 *   `siteListsUpdated` (UI/metadata signals, not design mutations)
 * - non-design UI actions: `domainConnect`, `domainConnectBlog` (handled in
 *   `src/hooks/query/useSite.js`, unrelated to design data)
 * Payload shapes below are documented per the *real* handler signature
 * (verified against each action's `on<Action>` method in
 * `src/stores/SiteDesignStore.js` or `src/stores/ClipboardStore.js`, and
 * against real call sites). Most actions take a single object payload,
 * passed through unchanged. A few take positional arguments; for those the
 * command payload is an array spread with `...p` in `commandMap.js` (see
 * that file's inline notes) — documented here as "positional".
 * Four commands (`ADD_ELEMENT_FROM_UPLOADING_ASSET`, `DUPLICATE_ELEMENT_GROUP`,
 * `REMOVE_ELEMENT_GROUP`, `TOGGLE_ELEMENT_GROUP_LOCK`) have no listener
 * anywhere in the app today (dead/unwired actions) - included for
 * completeness since they are declared as design mutations in SiteActions.js;
 * dispatching them is a no-op today, identical to calling the action directly.
 * @enum {string}
 */
export const COMMANDS = {
  // -- design / page --
  /** payload: object (design data), see SiteDesignStore#onSetDesign */
  SET_DESIGN: "setDesign",
  /** payload: object, see SiteDesignStore#onUpdateDesign (vo/DesignUpdate) */
  UPDATE_DESIGN: "updateDesign",
  /** payload: object, see SiteDesignStore#onSaveJSON */
  SAVE_JSON: "saveJSON",
  /** payload: object, see SiteDesignStore#onAddPage */
  ADD_PAGE: "addPage",
  /** payload: object, see SiteDesignStore#onRemovePage */
  REMOVE_PAGE: "removePage",
  /** payload: object, see SiteDesignStore#onDuplicatePage */
  DUPLICATE_PAGE: "duplicatePage",
  /** payload: object (vo/PageUpdate), see SiteDesignStore#onUpdatePage */
  UPDATE_PAGE: "updatePage",
  /** payload: object, see SiteDesignStore#onUpdatePageName */
  UPDATE_PAGE_NAME: "updatePageName",
  /** payload: object, see SiteDesignStore#onUpdatePageWpTemplate */
  UPDATE_PAGE_WP_TEMPLATE: "updatePageWpTemplate",
  /** payload: pageId (scalar), see SiteDesignStore#onTogglePageIsPublishable */
  TOGGLE_PAGE_IS_PUBLISHABLE: "togglePageIsPublishable",
  /** payload: object, see SiteDesignStore#onSetAsHomePage */
  SET_AS_HOME_PAGE: "setAsHomePage",
  /** payload: object, see SiteDesignStore#onAddFolder */
  ADD_FOLDER: "addFolder",
  /** payload: object, see SiteDesignStore#onRemoveFolder */
  REMOVE_FOLDER: "removeFolder",
  /** payload: object, see SiteDesignStore#onUpdateTheme */
  UPDATE_THEME: "updateTheme",

  // -- global code / code page exceptions --
  /** payload: object (embed), see SiteDesignStore#onAddGlobalCode */
  ADD_GLOBAL_CODE: "addGlobalCode",
  /** payload: object (embed), see SiteDesignStore#onUpdateGlobalCode */
  UPDATE_GLOBAL_CODE: "updateGlobalCode",
  /** payload: id (scalar), see SiteDesignStore#onRemoveGlobalCode */
  REMOVE_GLOBAL_CODE: "removeGlobalCode",
  /**
   * payload: positional [pageId, includeInGlobalCode], see
   * SiteDesignStore#onAddCodePageException. No current call sites (dead
   * but wired); pass-through preserved.
   */
  ADD_CODE_PAGE_EXCEPTION: "addCodePageException",

  // -- canvas sets --
  /** payload: none (no-arg action), see SiteDesignStore#onCreateCanvasSet */
  CREATE_CANVAS_SET: "createCanvasSet",
  /** payload: {id}, see SiteDesignStore#onRemoveCanvasSet */
  REMOVE_CANVAS_SET: "removeCanvasSet",
  /** payload: {id}, see SiteDesignStore#onDuplicateCanvasSet */
  DUPLICATE_CANVAS_SET: "duplicateCanvasSet",
  /** payload: {id}, see SiteDesignStore#onMakeCanvasSetDefault */
  MAKE_CANVAS_SET_DEFAULT: "makeCanvasSetDefault",
  /** payload: object, see SiteDesignStore#onAddCanvasToCanvasSet */
  ADD_CANVAS_TO_CANVAS_SET: "addCanvasToCanvasSet",
  /** payload: object, see SiteDesignStore#onRemoveCanvasFromCanvasSet */
  REMOVE_CANVAS_FROM_CANVAS_SET: "removeCanvasFromCanvasSet",
  /** payload: object, see SiteDesignStore#onUpdateCanvasSet */
  UPDATE_CANVAS_SET: "updateCanvasSet",

  // -- blocks --
  /** payload: object, see SiteDesignStore#onAddBlock */
  ADD_BLOCK: "addBlock",
  /** payload: object, see SiteDesignStore#onRemoveBlock */
  REMOVE_BLOCK: "removeBlock",
  /** payload: object (block), see SiteDesignStore#onReplaceBlock */
  REPLACE_BLOCK: "replaceBlock",
  /** payload: object, see SiteDesignStore#onDuplicateBlock */
  DUPLICATE_BLOCK: "duplicateBlock",
  /** payload: object (vo/BlockUpdate), see SiteDesignStore#onUpdateBlock */
  UPDATE_BLOCK: "updateBlock",
  /** payload: object, see SiteDesignStore#onUpdateBlockName */
  UPDATE_BLOCK_NAME: "updateBlockName",
  /** payload: object, see SiteDesignStore#onCopyBlockIntoBlock */
  COPY_BLOCK_INTO_BLOCK: "copyBlockIntoBlock",
  /** payload: object, see SiteDesignStore#onConvertToSiteBlock */
  CONVERT_TO_SITE_BLOCK: "convertToSiteBlock",
  /** payload: object, see SiteDesignStore#onConvertToPageBlock */
  CONVERT_TO_PAGE_BLOCK: "convertToPageBlock",
  /** payload: object, see SiteDesignStore#onAddSiteBlock */
  ADD_SITE_BLOCK: "addSiteBlock",
  /** payload: object, see SiteDesignStore#onRemoveSiteBlock */
  REMOVE_SITE_BLOCK: "removeSiteBlock",
  /** payload: object, see SiteDesignStore#onReplaceSiteBlock */
  REPLACE_SITE_BLOCK: "replaceSiteBlock",

  // -- block states --
  /** payload: object, see SiteDesignStore#onAddBlockState */
  ADD_BLOCK_STATE: "addBlockState",
  /** payload: object, see SiteDesignStore#onRemoveBlockState */
  REMOVE_BLOCK_STATE: "removeBlockState",
  /** payload: object, see SiteDesignStore#onDuplicateBlockState */
  DUPLICATE_BLOCK_STATE: "duplicateBlockState",
  /** payload: object, see SiteDesignStore#onUpdateBlockStateName */
  UPDATE_BLOCK_STATE_NAME: "updateBlockStateName",

  // -- elements --
  /**
   * payload: object {element|elements, isNew, containedBy, blockId, ...},
   * see SiteDesignStore#onAddElement
   */
  ADD_ELEMENT: "addElement",
  /**
   * payload: object. No listener anywhere in the app today (dead/unwired);
   * pass-through preserved as a no-op, identical to current behavior.
   */
  ADD_ELEMENT_FROM_UPLOADING_ASSET: "addElementFromUploadingAsset",
  /**
   * payload: positional [elementId, skipSave], see
   * SiteDesignStore#onRemoveElement
   */
  REMOVE_ELEMENT: "removeElement",
  /**
   * payload: positional [elementId, options], see
   * SiteDesignStore#onDuplicateElement
   */
  DUPLICATE_ELEMENT: "duplicateElement",
  /**
   * payload: object. No listener anywhere in the app today (dead/unwired);
   * pass-through preserved as a no-op, identical to current behavior.
   */
  DUPLICATE_ELEMENT_GROUP: "duplicateElementGroup",
  /**
   * payload: object. No listener anywhere in the app today (dead/unwired);
   * pass-through preserved as a no-op, identical to current behavior.
   */
  REMOVE_ELEMENT_GROUP: "removeElementGroup",
  /**
   * payload: skipDissolve (scalar, optional), see
   * SiteDesignStore#onRemoveSelectedElements
   */
  REMOVE_SELECTED_ELEMENTS: "removeSelectedElements",
  /** payload: object, see SiteDesignStore#onUpdateElementName */
  UPDATE_ELEMENT_NAME: "updateElementName",
  /** payload: object, see SiteDesignStore#onUpdateItemName */
  UPDATE_ITEM_NAME: "updateItemName",
  /** payload: object, see SiteDesignStore#onAddDesignItem */
  ADD_DESIGN_ITEM: "addDesignItem",
  /**
   * payload: positional [element, elementId, aspectRatio], see
   * SiteDesignStore#onResizeToAspectRatio
   */
  RESIZE_TO_ASPECT_RATIO: "resizeToAspectRatio",

  // -- ordering / layout --
  /**
   * payload: positional [item, target], see SiteDesignStore#onMoveItem
   */
  MOVE_ITEM: "moveItem",
  /** payload: object, see SiteDesignStore#onMoveLayer */
  MOVE_LAYER: "moveLayer",
  /** payload: object, see SiteDesignStore#onSortBlockByReadingOrder */
  SORT_BLOCK_BY_READING_ORDER: "sortBlockByReadingOrder",
  /** payload: object, see SiteDesignStore#onSortPageByReadingOrder */
  SORT_PAGE_BY_READING_ORDER: "sortPageByReadingOrder",
  /** payload: none (no-arg action), see SiteDesignStore#onToggleItemsLockStatus */
  TOGGLE_ITEMS_LOCK_STATUS: "toggleItemsLockStatus",
  /**
   * payload: object. No listener anywhere in the app today (dead/unwired,
   * see the commented-out call in SiteDesignStore.js); pass-through
   * preserved as a no-op, identical to current behavior.
   */
  TOGGLE_ELEMENT_GROUP_LOCK: "toggleElementGroupLock",

  /**
   * payload: photo (Pexels photo object), see SiteDesignStore#onRecordRecentPexelsPhoto.
   * Writes the site's recent-photos list without an undo point of its own.
   */
  RECORD_RECENT_PEXELS_PHOTO: "recordRecentPexelsPhoto",

  // Unlike every other entry in this file, `groupSelection`/`dissolveGroup`/
  // `addToGroup`/`removeFromGroup`/`onResizeGroup` are NOT `SiteActions` --
  // legacy code calls them as PLAIN METHODS directly on the `SiteDesignStore`
  // singleton (see `src/utils/GroupUtils.js`), bypassing Reflux/`commandMap`
  // entirely (`onResizeGroup` is the one exception: it IS Reflux-wired, but
  // via `UIActions.resizeGroup`, not `SiteActions`). They have no
  // `commandMap.js` pass-through entry for the same reason -- there is no
  // existing `SiteActions`/`UIActions` call for `dispatch()` to delegate to
  // that matches this table's one-call-per-command shape.
  /** payload: positional [elementSelection, blockId], see SiteDesignStore#groupSelection */
  GROUP_SELECTION: "groupSelection",
  /**
   * payload: positional [targetGroup, blockId, addUndo], see
   * SiteDesignStore#dissolveGroup. NOTE: this is a DIFFERENT payload SHAPE
   * than `SIDE_EFFECTS.DISSOLVE_GROUP`'s descriptor object
   * (`{targetGroup, blockId, addUndo}`) -- the descriptor shape was chosen to
   * match what emitting handlers already have in hand; the command payload
   * shape here is positional to match `dissolveGroup`'s real call signature
   * (so `parityHarness.runParity` can feed it straight to the legacy method).
   */
  DISSOLVE_GROUP: "dissolveGroup",
  /** payload: positional [targetGroup, elementSelection, blockId, insertAt], see SiteDesignStore#addToGroup */
  ADD_TO_GROUP: "addToGroup",
  /**
   * payload: positional [elementSelection, blockId, insertAt], see
   * SiteDesignStore#removeFromGroup. NOTE: legacy's `insertAt` parameter is
   * declared but never referenced in the method body -- a real, faithfully-
   * replicated dead parameter, not an oversight in this payload shape.
   */
  REMOVE_FROM_GROUP: "removeFromGroup",
  /**
   * payload: positional [idOrPath, isFocused], see SiteDesignStore#onResizeGroup.
   * `idOrPath` is legacy's own (only) argument; `isFocused` is an INJECTED
   * extra (mirrors `GroupStore.isFocused()`, a cross-store read the owner
   * cannot perform) -- passing it as a second positional array element is
   * harmless for the legacy side (`onResizeGroup(idOrPath)` simply ignores
   * an extra argument), so both `runLegacyHandler`/the owner handler can
   * share this exact payload via `parityHarness.runParity`.
   */
  RESIZE_GROUP: "resizeGroup",

  // -- clipboard --
  /** payload: object {insertAt, type, select, canvasPosition}, see ClipboardStore#onPaste */
  PASTE: "paste",
  /** payload: object {type, id}, see ClipboardStore#onPasteInto */
  PASTE_INTO: "pasteInto",
  /** payload: theme (Immutable Map), see ClipboardStore#onSpecialPaste */
  SPECIAL_PASTE: "specialPaste",

  // -- history --
  /** payload: none (no-arg action), see SiteDesignStore#onUndo */
  UNDO: "undo",
  /** payload: none (no-arg action), see SiteDesignStore#onRedo */
  REDO: "redo",
};
